import { useCallback, useState } from "react";
import { formatTimeNoSeconds } from "../utils/dateTime";
import { validateZipAttachment } from "../utils/e2eAttachments";
import { validateRequiredQuestions } from "../utils/e2eRequiredValidation";

export default function useE2ESubmissionEditor({
  initialAnswers = {},
  initialAttachments = [],
} = {}) {
  const [answers, setAnswers] = useState(initialAnswers);
  const [attachments, setAttachments] = useState(initialAttachments);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [draftMessage, setDraftMessage] = useState("");
  const [uploadError, setUploadError] = useState("");
  // Tracks which questionIds failed validation — used by QuestionItem for checkbox errors
  const [validationErrors, setValidationErrors] = useState(new Set());

  const handleAnswerChange = useCallback((questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
    // Clear this question's validation error when user interacts
    setValidationErrors((prev) => {
      if (!prev.has(questionId)) return prev;
      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
  }, []);

  const validateRequired = useCallback(
    (sections = []) => {
      const validation = validateRequiredQuestions(sections, answers);
      if (!validation.isValid) {
        setValidationErrors(new Set(validation.missingQuestionIds));
        return false;
      }
      setValidationErrors(new Set());
      return true;
    },
    [answers]
  );

  const validateZipFile = useCallback((file) => {
    const validationError = validateZipAttachment(file);
    if (validationError) {
      setUploadError(validationError);
      return false;
    }
    return true;
  }, []);

  const markDraftSavedNow = useCallback(() => {
    setDraftMessage(`Utkast sparat ${formatTimeNoSeconds()}`);
  }, []);

  return {
    answers,
    setAnswers,
    attachments,
    setAttachments,
    submitting,
    setSubmitting,
    error,
    setError,
    draftMessage,
    setDraftMessage,
    uploadError,
    setUploadError,
    validationErrors,
    handleAnswerChange,
    validateRequired,
    validateZipFile,
    markDraftSavedNow,
  };
}